/**
 * Route Workspace (Wave R2.2) — replaces the LCR Management page.
 * One view: prefix → ordered carriers (cost + live ASR/MOS), simulation,
 * preview-before-apply rebuilds. Backend: /api/v1/routing/*.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SimulateIcon from "@mui/icons-material/Route";
import RefreshIcon from "@mui/icons-material/Refresh";
import BuildIcon from "@mui/icons-material/Build";
import AddIcon from "@mui/icons-material/Add";
import apiClient from "../api/client";
import PageHeader from "../components/common/PageHeader";
import SupplierScorecards from "../components/routing/SupplierScorecards";

interface CarrierChip {
  supplier_id: string;
  name: string;
  rate: number;
  /** P0-2: quality-blended cost = rate / ASR (null when no sample) */
  eff_rate?: number | null;
  address: string;
  calls: number;
  asr: number | null;
  mos: number | null;
  /** Channel cap for this lane (null = unlimited, 0 = blocked) */
  max_concurrent_channels?: number | null;
}

interface RouteRow {
  prefix: string;
  strategy?: string;
  leg_timeout_sec?: number;
  after_hours_strategy?: string | null;
  schedule_start?: string | null;
  schedule_end?: string | null;
  schedule_days?: string | null;
  carriers: CarrierChip[];
  cheapest: number;
  /** P0-2: suppliers paused by the quality gate for this prefix */
  excluded?: { name: string; reason: string | null; asr: number | null; mos: number | null; calls: number }[];
}

interface RoutingProfile {
  id: string;
  name: string;
  description: string | null;
  dr_group: number;
  supplier_pins: string[];
}

interface SimResult {
  destination: string;
  matched_prefix: string;
  first_carrier: string | null;
  first_carrier_cost: number;
  ordered_carriers: { name: string; rate: number; eff_rate?: number | null; address: string; asr: number | null; mos: number | null }[];
  excluded?: { name: string; reason: string | null }[];
}

export default function RouteWorkspace() {
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Simulation
  const [simDest, setSimDest] = useState("");
  const [simResult, setSimResult] = useState<SimResult | null>(null);
  const [simError, setSimError] = useState("");

  // Rebuild
  const [rebuilding, setRebuilding] = useState(false);

  // Route editor (create/edit route = prefix + carrier costs)
  const [routeRates, setRouteRates] = useState<{ supplier_id: string; rate: string; max_concurrent_channels: string }[]>([]);
  const [routeEditorOpen, setRouteEditorOpen] = useState(false);
  const [routePrefix, setRoutePrefix] = useState("");
  const [savingRoute, setSavingRoute] = useState(false);

  const openNewRoute = () => {
    setRoutePrefix("");
    setRouteRates([{ supplier_id: suppliers[0]?.id || "", rate: "", max_concurrent_channels: "" }]);
    setRouteEditorOpen(true);
  };

  const openEditRoute = async (prefix: string) => {
    try {
      const r = await apiClient.get(`/api/v1/routing/routes/${encodeURIComponent(prefix)}`);
      setRoutePrefix(prefix);
      setRouteRates((r.data.rates || []).map((x: any) => ({ supplier_id: x.supplier_id, rate: String(x.rate), max_concurrent_channels: x.max_concurrent_channels === null || x.max_concurrent_channels === undefined ? "" : String(x.max_concurrent_channels) })));
      setRouteEditorOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load route");
    }
  };

  const saveRoute = async () => {
    const rates = routeRates
      .filter((r) => r.supplier_id && r.rate)
      .map((r) => ({
        supplier_id: r.supplier_id,
        rate: parseFloat(r.rate),
        max_concurrent_channels:
          r.max_concurrent_channels === ""
            ? null
            : Math.max(0, parseInt(r.max_concurrent_channels, 10) || 0),
      }));
    if (!routePrefix.trim() || rates.length === 0) return;
    setSavingRoute(true);
    try {
      await apiClient.put(`/api/v1/routing/routes/${encodeURIComponent(routePrefix.trim())}`, { rates });
      setRouteEditorOpen(false);
      await loadTable();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save route");
    } finally {
      setSavingRoute(false);
    }
  };

  const deleteRoute = async () => {
    if (!routePrefix) return;
    setSavingRoute(true);
    try {
      await apiClient.delete(`/api/v1/routing/routes/${encodeURIComponent(routePrefix)}`);
      setRouteEditorOpen(false);
      await loadTable();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete route");
    } finally {
      setSavingRoute(false);
    }
  };

  // RT-2: strategy editor
  const [policyPrefix, setPolicyPrefix] = useState<string | null>(null);

  // RT-3: routing profiles
  const [profiles, setProfiles] = useState<RoutingProfile[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", description: "", supplier_pins: [] as string[] });
  const [savingProfile, setSavingProfile] = useState(false);
  const [policyForm, setPolicyForm] = useState({
    strategy: "lcr", leg_timeout_sec: "90",
    after_hours_strategy: "", schedule_start: "", schedule_end: "", schedule_days: "MO,TU,WE,TH,FR",
  });
  // RT-5: test call + analytics
  const [testDest, setTestDest] = useState("");
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [analytics, setAnalytics] = useState<any[]>([]);
  const [analyticsDays] = useState(7);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [rebuildMsg, setRebuildMsg] = useState("");

  const loadTable = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/v1/routing/table");
      setRoutes(response.data.routes || []);
      // RT-3: profiles + suppliers for the profile editor
      apiClient.get("/api/v1/routing/profiles").then((r) => setProfiles(r.data.profiles || [])).catch(() => {});
      loadAnalytics();
      apiClient.get("/api/v1/suppliers/").then((r) => {
        const items = r.data?.suppliers || r.data?.items || [];
        setSuppliers(items.map((s: any) => ({ id: s.id, name: s.name })));
      }).catch(() => {});
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load route table");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTable();
  }, [loadTable]);

  const runSimulation = async () => {
    if (!simDest || simDest.length < 2) {
      setSimError("Enter at least 2 digits");
      return;
    }
    setSimError("");
    try {
      const response = await apiClient.get(
        `/api/v1/routing/simulate?destination=${encodeURIComponent(simDest)}`
      );
      setSimResult(response.data);
    } catch (err: any) {
      setSimResult(null);
      setSimError(err.response?.data?.detail || "No route matches");
    }
  };

  const openPolicy = (row: RouteRow) => {
    setPolicyPrefix(row.prefix);
    setPolicyForm({
      strategy: row.strategy || "lcr",
      leg_timeout_sec: String(row.leg_timeout_sec ?? 90),
      after_hours_strategy: row.after_hours_strategy || "",
      schedule_start: row.schedule_start || "",
      schedule_end: row.schedule_end || "",
      schedule_days: row.schedule_days || "MO,TU,WE,TH,FR",
    });
  };

  const loadAnalytics = useCallback(async (days = analyticsDays) => {
    try {
      const r = await apiClient.get("/api/v1/routing/analytics", { params: { days } });
      setAnalytics(r.data.rows || []);
    } catch {
      // analytics optional
    }
  }, [analyticsDays]);

  const runTestCall = async () => {
    if (!testDest.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiClient.post("/api/v1/routing/test-call", { destination: testDest.trim(), timeout: 25 });
      setTestResult(r.data);
    } catch (err: any) {
      setTestResult({ success: false, fs_result: err.response?.data?.detail || "Test call failed" });
    } finally {
      setTesting(false);
    }
  };

  const savePolicy = async () => {
    if (!policyPrefix) return;
    setSavingPolicy(true);
    try {
      await apiClient.put(`/api/v1/routing/policies/${encodeURIComponent(policyPrefix)}`, {
        strategy: policyForm.strategy,
        leg_timeout_sec: parseInt(policyForm.leg_timeout_sec, 10) || 90,
        after_hours_strategy: policyForm.after_hours_strategy || null,
        schedule_start: policyForm.schedule_start || null,
        schedule_end: policyForm.schedule_end || null,
        schedule_days: policyForm.schedule_days || null,
      });
      setPolicyPrefix(null);
      await loadTable();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  const saveProfile = async () => {
    if (!profileForm.name.trim() || profileForm.supplier_pins.length === 0) return;
    setSavingProfile(true);
    try {
      await apiClient.post("/api/v1/routing/profiles", {
        name: profileForm.name.trim(),
        description: profileForm.description.trim() || undefined,
        supplier_pins: profileForm.supplier_pins,
      });
      setProfileOpen(false);
      setProfileForm({ name: "", description: "", supplier_pins: [] });
      await loadTable();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteProfile = async (id: string) => {
    try {
      await apiClient.delete(`/api/v1/routing/profiles/${id}`);
      await loadTable();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete profile");
    }
  };

  const rebuild = async () => {
    setRebuilding(true);
    setRebuildMsg("");
    try {
      const response = await apiClient.post("/api/v1/routing/rebuild");
      const d = response.data;
      setRebuildMsg(`Route table rebuilt: ${d.gateways} gateways, ${d.rules} rules, reload ${d.reloaded ? "ok" : "FAILED"}.`);
      await loadTable();
    } catch (err: any) {
      setRebuildMsg(err.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        title="Route Workspace"
        subtitle="Destination prefixes → ordered carriers → live routing tables. Strategies, profiles, test calls and analytics on one page."
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTable} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" color="success" onClick={openNewRoute}>
              Add Route
            </Button>
            <Button
              variant="contained"
              startIcon={<BuildIcon />}
              onClick={rebuild}
              disabled={rebuilding}
            >
              {rebuilding ? "Rebuilding…" : "Rebuild route table"}
            </Button>
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {rebuildMsg && (
        <Alert severity={rebuildMsg.includes("FAILED") ? "error" : "success"} sx={{ mb: 2 }}>
          {rebuildMsg}
        </Alert>
      )}

      {/* Simulation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Simulate: which carrier would a call to this destination take?
        </Typography>
        <Box display="flex" gap={1} alignItems="flex-start">
          <TextField
            size="small"
            placeholder="e.g. 442071234567"
            value={simDest}
            onChange={(e) => setSimDest(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && runSimulation()}
            sx={{ width: 260 }}
          />
          <Button variant="outlined" startIcon={<SimulateIcon />} onClick={runSimulation}>
            Simulate
          </Button>
        </Box>
        {simError && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{simError}</Typography>}
        {simResult && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              Matched prefix <strong>{simResult.matched_prefix}</strong> → first
              carrier <strong>{simResult.first_carrier}</strong> at{" "}
              <strong>${simResult.first_carrier_cost?.toFixed(6)}/s</strong>
            </Typography>
            <Box sx={{ mt: 1 }}>
              {simResult.ordered_carriers.map((c, i) => (
                <Chip
                  key={i}
                  label={`${i + 1}. ${c.name} @ $${c.rate.toFixed(4)}`}
                  size="small"
                  sx={{ mr: 1, mb: 0.5 }}
                  color={i === 0 ? "primary" : "default"}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Route table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Prefix</TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Carriers (effective-cost order)</TableCell>
              <TableCell align="right">Cheapest $/sec</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {routes.map((row) => (
              <TableRow key={row.prefix} hover>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", cursor: "pointer", textDecoration: "underline dotted" }}
                    onClick={() => openEditRoute(row.prefix)}
                    title="Edit route carriers & costs"
                  >
                    {row.prefix}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${row.strategy || "lcr"} · ${row.leg_timeout_sec ?? 90}s${row.after_hours_strategy ? ` ⏰→${row.after_hours_strategy}` : ""}`}
                    size="small"
                    color={row.strategy === "balanced" ? "secondary" : row.strategy === "pbr" ? "warning" : "primary"}
                    variant="outlined"
                    onClick={() => openPolicy(row)}
                    title="Click to edit routing strategy"
                    sx={{ cursor: "pointer" }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                    {row.carriers.map((c, i) => (
                      <Chip
                        key={c.supplier_id + i}
                        label={
                          (c.eff_rate != null && Math.abs(c.eff_rate - c.rate) > 1e-9
                            ? `${i + 1}. ${c.name} $${c.rate.toFixed(6)}/s (eff $${c.eff_rate.toFixed(6)}/s)`
                            : `${i + 1}. ${c.name} $${c.rate.toFixed(6)}/s`) +
                          (c.max_concurrent_channels != null
                            ? ` · ${c.max_concurrent_channels === 0 ? "blocked" : `${c.max_concurrent_channels}ch`}`
                            : "")
                        }
                        size="small"
                        color={i === 0 ? "primary" : "default"}
                        variant={i === 0 ? "filled" : "outlined"}
                        title={
                          c.eff_rate != null && c.eff_rate !== c.rate
                            ? "Effective cost = rate / ASR (quality-blended)"
                            : undefined
                        }
                      />
                    ))}
                    {(row.excluded ?? []).map((x) => (
                      <Chip
                        key={`x-${x.name}`}
                        label={`⏸ ${x.name} — ${x.reason ?? "quality paused"}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        title={`Paused by quality gate: ASR ${x.asr ?? "—"}%, MOS ${x.mos ?? "—"} over ${x.calls} calls`}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right">${row.cheapest.toFixed(6)}</TableCell>
              </TableRow>
            ))}
            {!loading && routes.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No routes rendered yet. Import supplier rates (LCR → Cost
                    Preview) and click "Rebuild route table".
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        {routes.length} route{routes.length === 1 ? "" : "s"} rendered · dr_routing reload is restart-free
      </Typography>

      {/* RT-5: route test call + analytics */}
      <Paper sx={{ p: 2, mb: 3, mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6">Verification & Analytics</Typography>
          <Button size="small" variant="outlined" onClick={() => loadAnalytics()} disabled={testing}>
            Refresh analytics
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Test destination"
            value={testDest}
            onChange={(e) => setTestDest(e.target.value)}
            placeholder="e.g. 441792000001"
            sx={{ width: 220 }}
          />
          <Button variant="contained" size="small" onClick={runTestCall} disabled={testing || !testDest.trim()}>
            {testing ? "Calling…" : "Place route test call"}
          </Button>
          {testResult && (
            <Chip
              size="small"
              color={testResult.success ? "success" : "error"}
              label={testResult.success
                ? `OK via ${testResult.carrier_hop || "?"} (group ${testResult.route_group || "?"})`
                : `FAILED: ${String(testResult.fs_result).slice(0, 60)}${testResult.carrier_hop ? ` (via ${testResult.carrier_hop})` : ""}`}
            />
          )}
        </Box>
        {analytics.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Prefix</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">ASR</TableCell>
                <TableCell align="right">ACD</TableCell>
                <TableCell align="right">MOS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.prefix}</TableCell>
                  <TableCell>{r.carrier}</TableCell>
                  <TableCell align="right">{r.calls}</TableCell>
                  <TableCell align="right">{(r.asr * 100).toFixed(0)}%</TableCell>
                  <TableCell align="right">{r.acd}s</TableCell>
                  <TableCell align="right">{r.avg_mos ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No routed-call analytics yet (CDR carrier data appears after live calls).
          </Typography>
        )}
      </Paper>

      {/* C2: supplier scorecards — ASR trend/PDD/margin/failures per supplier */}
      <SupplierScorecards />

      {/* RT-3: customer routing profiles */}
      <Paper sx={{ p: 2, mb: 3, mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6">Customer Routing Profiles</Typography>
          <Button size="small" variant="outlined" onClick={() => setProfileOpen(true)}>
            New Profile
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pin accounts to a fixed carrier set (IvozProvider-style override). Accounts without a
          profile use the brand-default table above. Assign profiles in the Accounts edit dialog.
        </Typography>
        {profiles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No profiles — all accounts use brand default routing.</Typography>
        ) : (
          profiles.map((pr) => (
            <Box key={pr.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}>
              <Chip label={pr.name} size="small" color="secondary" />
              <Typography variant="caption" color="text.secondary">group {pr.dr_group}</Typography>
              <Box sx={{ flex: 1, display: "flex", gap: 0.5 }}>
                {pr.supplier_pins.map((sid) => (
                  <Chip key={sid} label={suppliers.find((s) => s.id === sid)?.name || sid} size="small" variant="outlined" />
                ))}
              </Box>
              <Button size="small" color="error" onClick={() => deleteProfile(pr.id)}>Delete</Button>
            </Box>
          ))
        )}
      </Paper>

      {/* RT-3: profile create dialog */}
      <Dialog open={profileOpen} onClose={() => !savingProfile && setProfileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Routing Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Profile name"
              fullWidth
              required
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              placeholder="e.g. Premium tier"
            />
            <TextField
              label="Description"
              fullWidth
              value={profileForm.description}
              onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
            />
            <TextField
              select
              label="Pinned carriers (order = priority)"
              fullWidth
              SelectProps={{ multiple: true, value: profileForm.supplier_pins, onChange: (e: any) => setProfileForm({ ...profileForm, supplier_pins: e.target.value }) }}
              helperText="Ctrl/Cmd-click to select multiple; the order defines failover order"
            >
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)} disabled={savingProfile}>Cancel</Button>
          <Button onClick={saveProfile} variant="contained" disabled={savingProfile || !profileForm.name.trim() || profileForm.supplier_pins.length === 0}>
            Create & Rebuild
          </Button>
        </DialogActions>
      </Dialog>

      {/* Route editor: prefix + carrier costs */}
      <Dialog open={routeEditorOpen} onClose={() => !savingRoute && setRouteEditorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{routePrefix && routeRates.length ? `Edit Route — ${routePrefix}` : "Add Route"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Destination prefix"
              required
              fullWidth
              value={routePrefix}
              onChange={(e) => setRoutePrefix(e.target.value)}
              placeholder="e.g. 44 or 86"
              helperText="Digits (optionally + * #). Longest prefix wins: 44 beats 4"
              disabled={!!routeRates.length && routePrefix !== "" && routeRates.length > 0 && false}
            />
            {routeRates.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 150px 110px 40px",
                  gap: 1,
                  alignItems: "start",
                }}
              >
                <TextField
                  select
                  label="Carrier"
                  fullWidth
                  value={row.supplier_id}
                  onChange={(e) => setRouteRates(routeRates.map((r, j) => j === i ? { ...r, supplier_id: e.target.value } : r))}
                >
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Cost $/sec"
                  type="number"
                  fullWidth
                  inputProps={{ step: 0.000001, min: 0.000001 }}
                  value={row.rate}
                  onChange={(e) => setRouteRates(routeRates.map((r, j) => j === i ? { ...r, rate: e.target.value } : r))}
                />
                <TextField
                  label="Ch. Cap"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  value={row.max_concurrent_channels}
                  onChange={(e) => setRouteRates(routeRates.map((r, j) => j === i ? { ...r, max_concurrent_channels: e.target.value } : r))}
                />
                {routeRates.length > 1 ? (
                  <Button
                    size="small"
                    color="error"
                    sx={{ mt: 1, minWidth: 40 }}
                    onClick={() => setRouteRates(routeRates.filter((_, j) => j !== i))}
                    aria-label="Remove carrier"
                  >
                    ✕
                  </Button>
                ) : (
                  <Box sx={{ mt: 1, width: 40 }} />
                )}
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setRouteRates([...routeRates, { supplier_id: suppliers[0]?.id || "", rate: "", max_concurrent_channels: "" }])}
              >
                Add carrier
              </Button>
              <Typography variant="caption" color="text.secondary">
                Lower cost = tried first (LCR)
              </Typography>
            </Box>
            <Alert severity="info">
              Carriers need at least one active gateway to be routable. Strategy for this prefix
              (LCR/PBR/Balanced) is set via the strategy chip after saving.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          {routeRates.length > 0 && routePrefix && (
            <Button color="error" onClick={deleteRoute} disabled={savingRoute}>Delete Route</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setRouteEditorOpen(false)} disabled={savingRoute}>Cancel</Button>
          <Button onClick={saveRoute} variant="contained" disabled={savingRoute || !routePrefix.trim()}>
            Save & Rebuild
          </Button>
        </DialogActions>
      </Dialog>

      {/* RT-2: routing strategy editor */}
      <Dialog open={policyPrefix !== null} onClose={() => !savingPolicy && setPolicyPrefix(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Routing Strategy — {policyPrefix}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              select
              label="Strategy"
              fullWidth
              value={policyForm.strategy}
              onChange={(e) => setPolicyForm({ ...policyForm, strategy: e.target.value })}
            >
              <MenuItem value="lcr">LCR — least cost (quality-blended)</MenuItem>
              <MenuItem value="pbr">PBR — priority-based (gateway priority)</MenuItem>
              <MenuItem value="balanced">Balanced — load distribution</MenuItem>
            </TextField>
            <TextField
              label="Per-leg timeout (seconds)"
              fullWidth
              type="number"
              inputProps={{ min: 10, max: 600 }}
              value={policyForm.leg_timeout_sec}
              onChange={(e) => setPolicyForm({ ...policyForm, leg_timeout_sec: e.target.value })}
              helperText="Applied to the supplier leg via X-Route-Attrs; on timeout, routing advances to the next carrier"
            />
            <TextField
              select
              label="After-hours strategy (optional)"
              fullWidth
              value={policyForm.after_hours_strategy}
              onChange={(e) => setPolicyForm({ ...policyForm, after_hours_strategy: e.target.value })}
              helperText="With a schedule below, this strategy applies outside business hours"
            >
              <MenuItem value="">— None (single strategy) —</MenuItem>
              <MenuItem value="lcr">LCR</MenuItem>
              <MenuItem value="pbr">PBR</MenuItem>
              <MenuItem value="balanced">Balanced</MenuItem>
            </TextField>
            {policyForm.after_hours_strategy && (
              <>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Window start (HH:MM)"
                    fullWidth
                    value={policyForm.schedule_start}
                    onChange={(e) => setPolicyForm({ ...policyForm, schedule_start: e.target.value })}
                    placeholder="09:00"
                  />
                  <TextField
                    label="Window end (HH:MM)"
                    fullWidth
                    value={policyForm.schedule_end}
                    onChange={(e) => setPolicyForm({ ...policyForm, schedule_end: e.target.value })}
                    placeholder="17:00"
                  />
                </Box>
                <TextField
                  label="Days"
                  fullWidth
                  value={policyForm.schedule_days}
                  onChange={(e) => setPolicyForm({ ...policyForm, schedule_days: e.target.value })}
                  helperText="Two-letter day codes: MO,TU,WE,TH,FR,SA,SU"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyPrefix(null)} disabled={savingPolicy}>Cancel</Button>
          <Button onClick={savePolicy} variant="contained" disabled={savingPolicy}>
            Save & Rebuild
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
